import { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { AxiosError } from 'axios';

import useUnregisteredKit from 'services/kits/useUnregisteredKit';

import {
  ContainerSizes,
  PageContainer,
  Spinfinity,
  Typography,
} from 'components';
import { PartnerLogo } from './PartnerLogo';
import { StepTracker } from './steps/StepTracker';
import {
  KitActivationState,
  PartnerKitStep,
  PartnerKitStepProps,
} from './types';

// Re-export types for backward compatibility
export type { KitActivationState, PartnerKitStep, PartnerKitStepProps };

interface KitActivationForPartnersProps {
  steps: readonly PartnerKitStep[];
}

const KitActivationForPartners = ({ steps }: KitActivationForPartnersProps) => {
  const kitsMatch = useMatch<'kitId', string>('/partners/:kitId');
  const kitId = kitsMatch?.params.kitId;
  const { data: kit, error, isLoading, isSuccess } = useUnregisteredKit(kitId);

  const [currentActivationStep, setCurrentActivationStep] = useState(0);

  const [activationState, setActivationState] = useState<KitActivationState>({
    tinyAccount: { id: '' },
    kit: {},
  });

  useEffect(() => {
    const updateActivationState = () => {
      if (kit) {
        setActivationState(prevState => ({
          ...prevState,
          kit: kit,
          tinyAccount: kit.tinyaccount
            ? kit.tinyaccount
            : prevState.tinyAccount,
        }));
      }
    };

    const checkAndUpdateStep = () => {
      const shouldSkipSurveys =
        kit?.partner?.portal_config?.skip_surveys ||
        kit?.partner?.portal_config?.survey_type === 'skip';
      if (kit?.tinyaccount && !kit?.date_survey_completed) {
        setCurrentActivationStep(1);
      } else if (kit?.tinyaccount && shouldSkipSurveys) {
        setCurrentActivationStep(2);
      }
    };
    updateActivationState();
    checkAndUpdateStep();
  }, [kit]);

  const StepComponent = steps[currentActivationStep]?.Component;

  const errorMessage =
    error instanceof AxiosError
      ? error.response?.data?.message || error.message
      : error?.message;

  return (
    <PageContainer
      size={ContainerSizes.SM}
      className='flex flex-column gap-2 p-4'
    >
      <StepTracker steps={steps} step={currentActivationStep} />
      {currentActivationStep !== 1 && (
        <div className='mt-4'>
          <PartnerLogo url={kit?.partner?.logo} />
        </div>
      )}
      {isLoading && <Spinfinity />}
      {(error ||
        (isSuccess &&
          !kit?.partner?.portal_config?.disable_kit_activation)) && (
        <div className='flex flex-column align-items-center gap-4 mt-4'>
          <Typography variant='body-s' className='red text-center'>
            {errorMessage ||
              'There is an error with your kit, please contact support at hello@tinyhealth.com'}
          </Typography>
        </div>
      )}
      {isSuccess && !!kit?.partner?.portal_config?.disable_kit_activation && (
        <div className='max-w-100'>
          {StepComponent && (
            <StepComponent
              step={currentActivationStep}
              setCurrentActivationStep={setCurrentActivationStep}
              activationState={activationState}
              setActivationState={setActivationState}
            />
          )}
        </div>
      )}
    </PageContainer>
  );
};

export default KitActivationForPartners;
